import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';
import { Logo } from '../components/Logo';
import { trackEvent } from '../lib/analytics';

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const [fullName, setFullName] = useState(searchParams.get('name') || '');
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [phone, setPhone] = useState(searchParams.get('phone') || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyNoticeAcknowledged, setPrivacyNoticeAcknowledged] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [productUpdatesOptIn, setProductUpdatesOptIn] = useState(false);
  const [role, setRole] = useState(searchParams.get('role') || 'worker');
  const [managerDpaConsent, setManagerDpaConsent] = useState(false);
  const [workerReliabilityConsent, setWorkerReliabilityConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Funnel: registration_started fires when the form mounts (one event per
  // browser session, regardless of role).
  useEffect(() => {
    trackEvent('registration_started', {
      role: searchParams.get('role') || 'worker',
      plan: searchParams.get('plan') || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      toast.error('Please accept the Terms of Service to continue.');
      return;
    }
    if (!privacyNoticeAcknowledged) {
      toast.error('Please acknowledge the Privacy Notice to continue.');
      return;
    }
    if (role === 'manager' && !managerDpaConsent) {
      toast.error('Please confirm the data processing consent to continue.');
      return;
    }
    if (role === 'worker' && !workerReliabilityConsent) {
      toast.error('Please confirm the reliability tracking acknowledgement to continue.');
      return;
    }
    setLoading(true);

    try {
      const roleExtra =
        role === 'manager'
          ? { managerDpaConsent: true }
          : { workerReliabilityConsent: true };
      // Consent & Legal Acceptance Audit Trail — send all four separate
      // records. Marketing / product-updates are ONLY sent as true if the
      // user actively ticked the box (positive-action-only).
      const extra = {
        ...roleExtra,
        termsAccepted: true,
        privacyNoticeAcknowledged: true,
        marketingOptIn: !!marketingOptIn,
        productUpdatesOptIn: !!productUpdatesOptIn,
      };
      const result = await register(fullName, email, phone, password, role, extra);
      const userData = result.user || result;

      trackEvent('registration_completed', {
        role: userData.role,
        plan: searchParams.get('plan') || null,
        reactivated: !!result.reactivated,
      });

      // New flow: backend requires email verification before login. There is no
      // JWT in the response — route the user to the "check your email" screen
      // and pass the email along so the resend button works.
      if (result.emailVerificationRequired) {
        const msg = result.reactivated
          ? "Account restored. We've sent a verification email — check your inbox to finish."
          : "Account created. We've sent a verification email — check your inbox to continue.";
        toast.success(msg);
        navigate('/check-email', { state: { email: userData.email } });
        return;
      }

      // Backward compatibility — if a token IS issued (legacy path), log in
      if (result.reactivated) {
        toast.success(`Welcome back, ${userData.fullName}! Your account has been restored.`);
      } else {
        toast.success(`Welcome, ${userData.fullName}! Your account has been created.`);
      }
      navigate(userData.role === 'manager' ? '/manager' : '/worker');
    } catch (error) {
      toast.error(formatApiError(error, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex flex-col">
      {/* Header */}
      <header className="bg-[#0f1b3d] text-white px-6 py-5">
        <Logo size="sm" variant="light" linkTo="/" />
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md shadow-lg border-0">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl font-bold text-slate-900 font-['Public_Sans']" data-testid="register-heading">
              How will you use ShiftCover365?
            </CardTitle>
            <CardDescription className="text-slate-500 text-[15px]">
              Choose your role, then create your account. Takes about a minute.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Phase 2 UX: two large selectable cards. Role can arrive
                  pre-selected via ?role= on the URL (from the landing-page
                  buttons) — the user can still change it here, but only
                  once, and never sees the question again. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup" aria-label="How will you use ShiftCover365?" data-testid="register-role-picker">
                <button
                  type="button"
                  role="radio"
                  aria-checked={role === 'manager'}
                  onClick={() => setRole('manager')}
                  className={`text-left p-4 rounded-2xl border-2 transition-colors min-h-[96px] focus:outline-none focus:ring-2 focus:ring-[#DA291C] ${role === 'manager' ? 'border-[#DA291C] bg-red-50/40' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                  data-testid="register-role-manager"
                >
                  <div className="flex items-start gap-2">
                    {role === 'manager' && <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-[#DA291C] text-white text-xs font-bold shrink-0 mt-0.5">✓</span>}
                    <div>
                      <p className="text-[16px] font-semibold text-slate-900 leading-tight">I manage a branch</p>
                      <p className="text-[13.5px] text-slate-500 mt-1">Find cover for my branch.</p>
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={role === 'worker'}
                  onClick={() => setRole('worker')}
                  className={`text-left p-4 rounded-2xl border-2 transition-colors min-h-[96px] focus:outline-none focus:ring-2 focus:ring-[#DA291C] ${role === 'worker' ? 'border-[#DA291C] bg-red-50/40' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                  data-testid="register-role-worker"
                >
                  <div className="flex items-start gap-2">
                    {role === 'worker' && <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-[#DA291C] text-white text-xs font-bold shrink-0 mt-0.5">✓</span>}
                    <div>
                      <p className="text-[16px] font-semibold text-slate-900 leading-tight">I want to work shifts</p>
                      <p className="text-[13.5px] text-slate-500 mt-1">Find available shifts near me.</p>
                    </div>
                  </div>
                </button>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-slate-700 font-medium text-[15px]">Full name</Label>
                <Input
                  id="fullName"
                  type="text"
                  placeholder="John Smith"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  autoComplete="name"
                  className="h-14 rounded-xl bg-slate-50 border-slate-200 focus:ring-[#0f1b3d] text-[16px]"
                  data-testid="register-fullname-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-700 font-medium text-[15px]">Mobile number</Label>
                <Input
                  id="phone"
                  type="tel"
                  inputMode="tel"
                  placeholder="07700 900000"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  autoComplete="tel"
                  className="h-14 rounded-xl bg-slate-50 border-slate-200 focus:ring-[#0f1b3d] text-[16px]"
                  data-testid="register-phone-input"
                />
                <p className="text-xs text-slate-500">
                  Only the manager or worker you&apos;re matched with sees this. We use it for shift-day contact only — never for marketing.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 font-medium text-[15px]">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  inputMode="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="h-14 rounded-xl bg-slate-50 border-slate-200 focus:ring-[#0f1b3d] text-[16px]"
                  data-testid="register-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700 font-medium text-[15px]">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    className="h-14 rounded-xl bg-slate-50 border-slate-200 focus:ring-[#0f1b3d] text-[16px] pr-24"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[13px] font-semibold text-[#4338CA] hover:underline min-h-[44px] px-2"
                    data-testid="register-password-toggle"
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                <p className="text-[13px] text-slate-500 leading-relaxed" data-testid="register-password-requirements">
                  Use at least 8 characters. A mix of letters and numbers is stronger.
                </p>
              </div>

              {/* Consent & Legal Acceptance Audit Trail —
                  four SEPARATE consent/acknowledgement records:
                    1. Terms of Service — required (positive acceptance)
                    2. Privacy Notice — required acknowledgement (this is
                       NOT "consent to processing"; each processing purpose
                       has its own lawful basis, documented in the notice)
                    3. Marketing email — optional, unticked
                    4. Product updates — optional, unticked
                  All four map to immutable rows in `consent_records`. */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="legal-consent-block">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-1 h-5 w-5 rounded border-slate-300 text-[#DA291C] focus:ring-[#DA291C] shrink-0"
                    data-testid="terms-accepted-checkbox"
                    required
                  />
                  <span className="text-[14px] text-slate-700 leading-relaxed">
                    I accept the{' '}
                    <Link to="/legal/terms" className="text-[#4338CA] hover:underline font-medium">Terms of Service</Link>.
                    <span className="block text-[12px] text-slate-500 mt-0.5">Required to create an account.</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privacyNoticeAcknowledged}
                    onChange={(e) => setPrivacyNoticeAcknowledged(e.target.checked)}
                    className="mt-1 h-5 w-5 rounded border-slate-300 text-[#DA291C] focus:ring-[#DA291C] shrink-0"
                    data-testid="privacy-notice-ack-checkbox"
                    required
                  />
                  <span className="text-[14px] text-slate-700 leading-relaxed">
                    I acknowledge that I have been shown the{' '}
                    <Link to="/legal/privacy" className="text-[#4338CA] hover:underline font-medium">Privacy Notice</Link>.
                    <span className="block text-[12px] text-slate-500 mt-0.5">
                      This acknowledges the notice was presented — the lawful basis for each processing purpose is set out inside the notice itself.
                    </span>
                  </span>
                </label>
              </div>

              {/* Optional marketing choices — separate block, unticked by
                  default, positive-action-only. Easy withdrawal is available
                  later from Settings → Notifications. */}
              <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="marketing-consent-block">
                <p className="text-[13px] text-slate-500">
                  Optional — you can change these any time in your settings.
                </p>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={marketingOptIn}
                    onChange={(e) => setMarketingOptIn(e.target.checked)}
                    className="mt-1 h-5 w-5 rounded border-slate-300 text-[#DA291C] focus:ring-[#DA291C] shrink-0"
                    data-testid="marketing-optin-checkbox"
                  />
                  <span className="text-[14px] text-slate-700 leading-relaxed">
                    I&apos;d like to receive occasional marketing emails from ShiftCover365 (tips, offers, news).
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={productUpdatesOptIn}
                    onChange={(e) => setProductUpdatesOptIn(e.target.checked)}
                    className="mt-1 h-5 w-5 rounded border-slate-300 text-[#DA291C] focus:ring-[#DA291C] shrink-0"
                    data-testid="product-updates-optin-checkbox"
                  />
                  <span className="text-[14px] text-slate-700 leading-relaxed">
                    I&apos;d like to receive occasional product-update emails about new features and improvements.
                  </span>
                </label>
                <p className="text-[12px] text-slate-500 leading-relaxed">
                  Essential security, account and operational messages (like shift alerts) are separate from these choices and will still be sent.
                </p>
              </div>

              {role === 'manager' && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="manager-dpa-consent-block">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={managerDpaConsent}
                      onChange={(e) => setManagerDpaConsent(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-[#DA291C] focus:ring-[#DA291C] shrink-0"
                      data-testid="manager-dpa-consent-checkbox"
                      required
                    />
                    <span className="text-[12.5px] text-slate-700 leading-relaxed">
                      I confirm that I am authorised to register this branch on ShiftCover365 on behalf of my employing organisation. I acknowledge that by using this platform I will be processing personal data belonging to workers, and I agree to handle that data in accordance with UK GDPR. A copy of our Data Processing Agreement is available on request by emailing{' '}
                      <a href="mailto:support@shiftcover365.co.uk" className="text-[#4338CA] hover:underline font-medium">support@shiftcover365.co.uk</a>.
                    </span>
                  </label>
                </div>
              )}

              {role === 'worker' && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="worker-reliability-consent-block">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={workerReliabilityConsent}
                      onChange={(e) => setWorkerReliabilityConsent(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-[#DA291C] focus:ring-[#DA291C] shrink-0"
                      data-testid="worker-reliability-consent-checkbox"
                      required
                    />
                    <span className="text-[12.5px] text-slate-700 leading-relaxed">
                      I understand that my punctuality and reliability are tracked on ShiftCover365. Cancellations made within 24 hours of a shift start time and no-shows are recorded and will negatively affect my reliability score. Workers with persistently low reliability scores may be suspended from the platform. I agree to these terms as a condition of using the platform.
                    </span>
                  </label>
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-14 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white font-semibold text-lg mt-6 disabled:opacity-60"
                disabled={
                  loading ||
                  !termsAccepted ||
                  !privacyNoticeAcknowledged ||
                  (role === 'manager' && !managerDpaConsent) ||
                  (role === 'worker' && !workerReliabilityConsent)
                }
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create my account'
                )}
              </Button>
            </form>

            <p className="mt-6 text-center text-slate-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="text-[#DA291C] font-semibold hover:underline"
                data-testid="login-link"
              >
                Sign In
              </Link>
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
